import React, { useEffect, useId } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import { useTheme } from '@mui/material';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import _ from 'lodash';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { useAppSettings } from '@/app/features/settings/providers/AppSettingsProvider';
import styleVariables from '@/app/styles/styleVariables';
import useAppFormatNumber from '@/app/features/format/hooks/useAppFormatNumber';
import useAppFormatAmount from '@/app/features/format/hooks/useAppFormatAmount';
import { TInvestFundCurrency } from '@/apps/investor/types/fund/TInvestFundCurrency';

type CGraphAssetsSynthesisProps = {
	data: {
		category: string;
		value: number;
	}[];
	currency: TInvestFundCurrency;
};
const CGraphAssetsSynthesis = (props: CGraphAssetsSynthesisProps) => {
	const { data, currency } = props;
	const idGraph = useId();

	const theme = useTheme();

	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();
	const { formatAmount } = useAppFormatNumber();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const listColors = !darkModeEnabled
		? styleVariables.sliceColors.lightMode.map((x) => am5.color(x))
		: styleVariables.sliceColors.darkMode.map((x) => am5.color(x));

	useEffect(() => {
		const domainSynthesis = am5.Root.new(`assets-graph-synthesis-${idGraph}`);
		darkModeEnabled
			? domainSynthesis.setThemes([
					am5themes_Dark.new(domainSynthesis),
					am5themes_Responsive.new(domainSynthesis),
			  ])
			: domainSynthesis.setThemes([
					am5themes_Animated.new(domainSynthesis),
					am5themes_Responsive.new(domainSynthesis),
			  ]);

		const chartDomain = domainSynthesis.container.children.push(
			am5percent.PieChart.new(domainSynthesis, {
				layout: domainSynthesis.verticalLayout,
				innerRadius: am5.percent(85),
			}),
		);

		const series = chartDomain.series.push(
			am5percent.PieSeries.new(domainSynthesis, {
				valueField: 'value',
				categoryField: 'category',
				alignLabels: true,
			}),
		);

		chartDomain.seriesContainer.children.push(
			am5.Label.new(domainSynthesis, {
				textAlign: 'center',
				y: 26,
				centerY: am5.percent(100),
				fill: theme.palette.mode === 'dark' ? am5.color('#FFF') : am5.color('#1A2B48'),
				centerX: am5.p50,
				text: `[bold fontSize:32px]${formatAmount(
					displayRounding(_.sum(data.map((x) => x.value)), rounding),
					2,
					true,
				)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)}[/][/]`,
			}),
		);

		const sliceDomainTemplate = series.slices.template;
		sliceDomainTemplate.setAll({
			templateField: 'settings',
		});

		sliceDomainTemplate.set('toggleKey', 'none');
		sliceDomainTemplate.states.create('active', {
			shiftRadius: 0,
		});

		const sliceTooltipsDomainTemplate = series.slices.template;
		sliceTooltipsDomainTemplate.setAll({
			templateField: 'tooltipSettings',
		});

		series.data.setAll(
			data.map((item, index) => ({
				value: item.value,
				category: item.category,
				settings: {
					fill: listColors[index % listColors.length],
					stroke: 0,
				},
				tooltipSettings: {
					fill: listColors[index % listColors.length],
					stroke: listColors[index % listColors.length],

					tooltipText: `${item.category} (${formatAmount(
						displayRounding(item.value, rounding),
						2,
						true,
					)} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)})`,
				},
			})),
		);

		series.labels.template.set('visible', false);
		series.ticks.template.set('visible', false);

		series.appear(500, 100);

		return () => {
			domainSynthesis.dispose();
		};
	}, [
		currency,
		darkModeEnabled,
		data,
		displayCurrencySymbol,
		displayRounding,
		displayRoundingSuffix,
		formatAmount,
		idGraph,
		listColors,
		rounding,
		theme.palette.mode,
	]);

	return (
		<div>
			<div
				id={`assets-graph-synthesis-${idGraph}`}
				style={{ height: '450px', margin: '0px', padding: '0px', fontFamily: 'Poppins' }}
			/>
		</div>
	);
};

export { CGraphAssetsSynthesis };
